/*
 * Fernzugang zum Servicezugang (docs/servicezugang-oem-und-fern.md, Teil 3):
 * reine Logik ohne Browser, damit Suche und Rundung testbar sind. Gleiche
 * Regeln wie die Service-App am Panel (HVACloud.Client.Service: PointSearch,
 * Shell.Snap), damit am Panel und aus der Ferne dasselbe herauskommt.
 */

/** Ein Punkt, wie die lokale API ihn liefert (ServiceDataPoint). Leere
 *  Felder laesst der Client im JSON weg, deshalb optional: sie kommen als
 *  undefined, nicht als null (TP10, 22.10.2026). */
export interface ServicePoint {
  address: string;
  group: string;
  line?: string | null;
  label: string;
  valueType: string;
  unit?: string | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  enum?: { value: number; name: string; displayName?: string | null }[] | null;
  level: string;
  writable: boolean;
}

/** Eine Lesung (ServiceReading). */
export interface ServiceReading {
  address: string;
  value: unknown;
  text: string | null;
  at: string;
  quality: string;
  error?: string | null;
}

/** Punkt mit letzter Lesung (ServicePointView). */
export interface ServicePointView {
  point: ServicePoint;
  last: ServiceReading | null;
  ageSeconds: number | null;
}

/** Antwort auf eine Schreibung (ServiceWriteView). */
export interface ServiceWriteView {
  ok: boolean;
  error: string | null;
  old: ServiceReading | null;
  new: ServiceReading | null;
  protocolId: string;
}

/**
 * Klein, ohne Akzente, ß als ss, ae/oe/ue als a/o/u; alles außer Buchstaben
 * und Ziffern wird Leerzeichen. Beide Seiten gleich behandelt, deshalb finden
 * "warme", "waerme" und "Wärme" dasselbe.
 */
export function normalizeSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]/gu, ' ')
    .replace(/ae/g, 'a')
    .replace(/oe/g, 'o')
    .replace(/ue/g, 'u');
}

/**
 * Suche über alle Punkte nach Bediennummer und Bezeichnung. Jedes Wort muss
 * vorkommen. Reihenfolge: Nummer genau, Nummer beginnt so, Bezeichnung
 * beginnt so, irgendwo; innerhalb gleich wie im Katalog.
 */
export function findPoints<T extends { point: Pick<ServicePoint, 'line' | 'label'> }>(
  rows: readonly T[], query: string, limit: number): T[] {
  const words = normalizeSearch(query).split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return [];
  const first = words[0]!;
  const hits: { rank: number; index: number; row: T }[] = [];
  rows.forEach((row, index) => {
    const line = normalizeSearch(row.point.line ?? '');
    const label = normalizeSearch(row.point.label);
    const all = `${line} ${label}`;
    if (!words.every((w) => all.includes(w))) return;
    const rank = line === first ? 0 : line.startsWith(first) ? 1 : label.startsWith(first) ? 2 : 3;
    hits.push({ rank, index, row });
  });
  return hits.sort((a, b) => a.rank - b.rank || a.index - b.index).slice(0, limit).map((h) => h.row);
}

/**
 * Gemeinsames "Typ: " vor allen Gruppennamen, sonst null. Der Treiber stellt
 * bei mehreren Geräten am Bus jedem Namen den Reglertyp voran
 * ("RVS21.831F/127: Heizkreis 1"); einmal genügt. Wie CommonPrefix in der
 * Service-App am Panel.
 */
export function commonGroupPrefix(names: readonly string[]): string | null {
  let prefix: string | null = null;
  for (const name of names) {
    const i = name.indexOf(': ');
    if (i <= 0) return null;
    const p = name.slice(0, i + 2);
    if (prefix === null) prefix = p;
    else if (prefix !== p) return null;
  }
  return prefix;
}

/** Gruppenname ohne das gemeinsame Präfix. */
export function stripGroup(name: string, prefix: string | null): string {
  return prefix && name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

/** Eingabe als Zahl; Dezimalkomma erlaubt. Null, wenn keine Zahl. */
export function parseNumber(text: string): number | null {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '' || !/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Zahl als Text mit Punkt, höchstens sechs Nachkommastellen, ohne Nullen am Ende. */
export function formatPlain(n: number): string {
  const s = n.toFixed(6).replace(/\.?0+$/, '');
  return s === '-0' ? '0' : s;
}

/**
 * Auf die Stufe des Reglers runden, Ergebnis mit Punkt, wie es geschrieben
 * wird. Halbe Stufen von der Null weg, wie im Client (MidpointRounding.AwayFromZero).
 * Keine Zahl: unverändert (getrimmt) zurück.
 */
export function snap(text: string, step: number | null | undefined): string {
  const v = parseNumber(text);
  if (v === null) return text.trim();
  if (!step || step <= 0) return formatPlain(v);
  const q = v / step;
  // Gegen Gleitkommareste wie 16.6/0.015625 = 1062.3999999.
  const r = Math.sign(q) * Math.round(Math.abs(q) + 1e-9);
  return formatPlain(r * step);
}

/** Stufe lesbar: 1/64 statt 0,015625, sonst mit Komma. */
export function stepText(step: number): string {
  const inverse = 1 / step;
  if (Math.abs(inverse - Math.round(inverse)) < 1e-9 && inverse > 10) return `1/${Math.round(inverse)}`;
  return withComma(formatPlain(step));
}

/** Für die Anzeige mit Dezimalkomma; geschrieben wird mit Punkt. */
export function withComma(text: string): string {
  return /^-?\d+(\.\d+)?$/.test(text) ? text.replace('.', ',') : text;
}

/**
 * Prüft einen Wert gegen Typ und Grenzen. Gibt den Fehler in Worten oder
 * null. Der Client prüft dasselbe noch einmal; hier nur, damit niemand auf
 * eine Ablehnung warten muss.
 */
export function checkValue(point: ServicePoint, text: string): string | null {
  if (!point.writable) return 'Dieser Punkt ist nicht schreibbar.';
  if (point.valueType === 'Bool') return text === '0' || text === '1' ? null : 'Ein oder Aus wählen.';
  if (point.valueType === 'Enum') {
    if (text === '') return 'Einen Eintrag wählen.';
    return (point.enum ?? []).some((o) => String(o.value) === text) ? null : 'Unbekannter Eintrag.';
  }
  if (point.valueType !== 'Number') return text.trim() === '' ? 'Wert fehlt.' : null;
  const v = parseNumber(snap(text, point.step));
  if (v === null) return 'Keine Zahl.';
  if (point.min != null && v < point.min - 1e-9) return `Kleiner als ${withComma(formatPlain(point.min))}.`;
  if (point.max != null && v > point.max + 1e-9) return `Größer als ${withComma(formatPlain(point.max))}.`;
  return null;
}

/** Wert zum Schreiben: Zahl gerundet, sonst wie gewählt. */
export function valueToWrite(point: ServicePoint, text: string): string {
  return point.valueType === 'Number' ? snap(text, point.step) : text.trim();
}

/** Rohwert einer Lesung als Text mit Punkt (Bool als 1/0). */
export function rawValue(r: ServiceReading | null | undefined): string | null {
  if (!r) return null;
  const v = r.value;
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v ? '1' : '0';
  if (typeof v === 'number') return formatPlain(v);
  return String(v);
}

/** Name eines Rohwerts: Auswahl mit Namen, Ein/Aus, Zahl mit Komma und Einheit. */
export function displayValue(point: ServicePoint, raw: string | null, text?: string | null): string {
  if (raw === null) return text ?? '–';
  if (point.valueType === 'Bool') return raw === '0' ? 'Aus' : 'Ein';
  if (point.valueType === 'Enum') {
    const o = (point.enum ?? []).find((e) => String(e.value) === raw);
    if (o) return o.displayName || o.name;
    return text ?? raw;
  }
  const shown = withComma(text && point.valueType !== 'Number' ? text : raw);
  return point.unit ? `${shown} ${point.unit}` : shown;
}

/** Alter einer Lesung, kurz. */
export function formatAge(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return 'nie gelesen';
  if (seconds < 1) return 'gerade eben';
  if (seconds < 60) return `vor ${Math.round(seconds)} s`;
  if (seconds < 3600) return `vor ${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `vor ${Math.round(seconds / 3600)} h`;
  return `vor ${Math.round(seconds / 86_400)} d`;
}

/** Alter aus dem Zeitstempel der Lesung. */
export function ageOf(r: ServiceReading | null | undefined, now: number): number | null {
  if (!r?.at) return null;
  const t = Date.parse(r.at);
  return Number.isNaN(t) ? null : Math.max(0, (now - t) / 1000);
}

/** Ebene lesbar. */
export function levelName(level: string): string {
  switch (level.toLowerCase()) {
    case 'endbenutzer': return 'Endbenutzer';
    case 'inbetriebsetzung': return 'Inbetriebsetzung';
    case 'fachmann': return 'Fachmann';
    case 'oem': return 'OEM';
    default: return level;
  }
}

/** Quelle einer Protokollzeile. */
export function grantSourceText(row: { grantSource: string; stickId: string | null; remoteUser?: string | null }): string {
  if (row.grantSource === 'fern') return `Fern: ${row.remoteUser ?? 'unbekannt'}`;
  if (row.grantSource === 'stick') return `Stick ${row.stickId ?? ''}`.trim();
  return 'PIN';
}

/** Eine Zeile des Schreibprotokolls, wie GET /api/plants/{id}/service-protocol sie liefert. */
export interface ServiceProtocolRow {
  id: string;
  clientId: string;
  line: string | null;
  label: string;
  group: string;
  level: string;
  unit: string | null;
  oldValue: string | null;
  oldText: string | null;
  requested: string;
  newValue: string | null;
  newText: string | null;
  ok: boolean;
  error: string | null;
  grantSource: string;
  stickId: string | null;
  remoteUser?: string | null;
  rollbackOf: string | null;
  at: string;
}

/** Zeile der Befehls-Historie: Befehl aus dem Betriebsschema oder Schreibung im OEM-Modus. */
export interface HistoryRow {
  id: string;
  at: string;
  /** Was geschrieben wurde: Befehlsname oder "Schreiben" bzw. "Rücknahme". */
  action: string;
  /** Datenpunkt: Leaf-Id oder Bediennummer und Bezeichnung. */
  target: string;
  targetTitle: string;
  value: string;
  status: string;
  tone: 'ok' | 'warn' | 'error' | 'info' | 'neutral';
  statusTitle: string | null;
  /** Nur im OEM-Modus: Freigabe, etwa "Fern: name", "Stick …", "PIN". */
  oem: string | null;
}

function shown(value: string | null, text: string | null, unit: string | null): string {
  const raw = text ?? value;
  if (raw === null || raw === '') return '–';
  // Der Treiber meldet Zahlen auch als Text mit Punkt ("25.5"); dann wie
  // jede Zahl mit Komma und Einheit, Auswahltexte unveraendert.
  const numeric = /^-?\d+(\.\d+)?$/.test(raw);
  return numeric ? `${withComma(raw)}${unit ? ` ${unit}` : ''}` : raw;
}

/** Schreibprotokoll als Zeile der Befehls-Historie, gekennzeichnet als OEM-Modus. */
export function protocolToHistoryRow(r: ServiceProtocolRow): HistoryRow {
  return {
    id: `oem-${r.id}`,
    at: r.at,
    action: r.rollbackOf ? 'Rücknahme' : 'Schreiben',
    target: `${r.line ? `${r.line} ` : ''}${r.label}`,
    targetTitle: `${r.group} · Ebene ${levelName(r.level)}`,
    value: `${shown(r.oldValue, r.oldText, r.unit)} → ${r.ok ? shown(r.newValue, r.newText, r.unit) : shown(r.requested, null, r.unit)}`,
    status: r.ok ? 'Executed' : 'Failed',
    tone: r.ok ? 'ok' : 'error',
    statusTitle: r.error,
    // Die Stick-Kennung ist eine Guid; in der Tabelle reicht ihr Anfang.
    oem: r.grantSource === 'stick' && r.stickId && r.stickId.length > 12
      ? `Stick ${r.stickId.slice(0, 8)}…`
      : grantSourceText(r),
  };
}
